import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import "../CustomTheme/customTheme.css";
import "./CustomModal.css";

// Open modals, newest last: Esc only closes the top one, and the page
// stays scroll-locked until the last modal closes.
const openStack = [];
let savedBodyOverflow = "";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * CustomModal
 *
 *   <CustomModal open={open} onClose={() => setOpen(false)} title="Edit Section"
 *     footer={<><CustomButton variant="outline" onClick={close}>Cancel</CustomButton>
 *              <CustomButton onClick={save}>Save</CustomButton></>}>
 *     ...form...
 *   </CustomModal>
 *
 * Props:
 *  - open, onClose
 *  - title, subtitle, icon
 *  - size              "sm" | "md" (default) | "lg" | "xl" | "full"
 *  - footer            buttons row (right-aligned; stacks on phones)
 *  - closeOnBackdrop   default true
 *  - closeOnEsc        default true
 *  - showClose         show the × button (default true)
 *  - bodyClassName, className
 */
const CustomModal = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  size = "md",
  footer,
  closeOnBackdrop = true,
  closeOnEsc = true,
  showClose = true,
  bodyClassName,
  className,
  children,
}) => {
  const titleId = useId();
  const dialogRef = useRef(null);
  const tokenRef = useRef({});
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const token = tokenRef.current;
    const previouslyFocused = document.activeElement;

    if (openStack.length === 0) {
      savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    openStack.push(token);

    // Move focus into the dialog: first field in the body, else the dialog itself
    // (not the × button, so Enter/typing goes to the form)
    const focusTimer = setTimeout(() => {
      const dialog = dialogRef.current;
      const target = dialog?.querySelector(`.cmd-body ${FOCUSABLE.split(", ").join(", .cmd-body ")}`) || dialog;
      target?.focus?.();
    }, 0);

    const onKeyDown = (e) => {
      if (openStack[openStack.length - 1] !== token) return; // not the top modal
      if (e.key === "Escape" && closeOnEsc) {
        e.stopPropagation();
        onCloseRef.current?.();
      }
      // Keep Tab inside the dialog
      if (e.key === "Tab" && dialogRef.current) {
        const items = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      const index = openStack.indexOf(token);
      if (index > -1) openStack.splice(index, 1);
      if (openStack.length === 0) document.body.style.overflow = savedBodyOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open, closeOnEsc]);

  if (!open) return null;

  return createPortal(
    <div
      className="cmd-backdrop"
      onMouseDown={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={`cmd-dialog cmd-${size}${className ? ` ${className}` : ""}`}
      >
        {(title || showClose) && (
          <div className="cmd-header">
            {icon && <span className="cmd-icon">{icon}</span>}
            <div className="cmd-titles">
              {title && (
                <h4 id={titleId} className="cmd-title">
                  {title}
                </h4>
              )}
              {subtitle && <p className="cmd-subtitle">{subtitle}</p>}
            </div>
            {showClose && (
              <button type="button" className="cmd-close" onClick={onClose} aria-label="Close">
                <X size={18} />
              </button>
            )}
          </div>
        )}

        <div className={`cmd-body${bodyClassName ? ` ${bodyClassName}` : ""}`}>{children}</div>

        {footer && <div className="cmd-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default CustomModal;
